import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type Props = {
  P: any[];
  lang: "es" | "en";
  toast?: (msg: string, type?: string) => void;
};

type Settings = {
  featured_product_id: string | null;
  deal_product_id: string | null;
  deal_discount_override: number | null;
  headline_es: string;
  headline_en: string;
  subtitle_es: string;
  subtitle_en: string;
  brand_name: string;
  brand_tagline_es: string;
  brand_tagline_en: string;
  brand_cta_es: string;
  brand_cta_en: string;
  brand_logo_url: string;
};

const empty: Settings = {
  featured_product_id: null,
  deal_product_id: null,
  deal_discount_override: null,
  headline_es: "",
  headline_en: "",
  subtitle_es: "",
  subtitle_en: "",
  brand_name: "",
  brand_tagline_es: "",
  brand_tagline_en: "",
  brand_cta_es: "",
  brand_cta_en: "",
  brand_logo_url: "",
};

const inputStyle: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--bd2)",
  background: "var(--bg2)",
  color: "var(--tx)",
  fontSize: 13,
  width: "100%",
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: "var(--tx3)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: 4,
  display: "block",
};

export function HeroEditor({ P, lang, toast }: Props) {
  const [s, setS] = useState<Settings>(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("hero_settings" as any)
        .select("*")
        .eq("id", 1)
        .maybeSingle();
      if (data) {
        setS({
          featured_product_id: (data as any).featured_product_id ?? null,
          deal_product_id: (data as any).deal_product_id ?? null,
          deal_discount_override: (data as any).deal_discount_override ?? null,
          headline_es: (data as any).headline_es ?? "",
          headline_en: (data as any).headline_en ?? "",
          subtitle_es: (data as any).subtitle_es ?? "",
          subtitle_en: (data as any).subtitle_en ?? "",
          brand_name: (data as any).brand_name ?? "",
          brand_tagline_es: (data as any).brand_tagline_es ?? "",
          brand_tagline_en: (data as any).brand_tagline_en ?? "",
          brand_cta_es: (data as any).brand_cta_es ?? "",
          brand_cta_en: (data as any).brand_cta_en ?? "",
          brand_logo_url: (data as any).brand_logo_url ?? "",
        });
      }
      setLoading(false);
    })();
  }, []);

  const brands = useMemo(() => {
    const set = new Set<string>();
    (P || []).forEach(p => { if (p.br) set.add(p.br); });
    return Array.from(set).sort();
  }, [P]);

  const productLabel = (p: any) =>
    `${typeof p.nm === "object" ? (p.nm[lang] || p.nm.en) : p.nm}${p.br ? ` — ${p.br}` : ""}`;

  const productOptions = useMemo(
    () => (P || []).slice().sort((a, b) => productLabel(a).localeCompare(productLabel(b))),
    [P, lang]
  );

  // Stored id: prefer dbId (real DB uuid) so it works regardless of catalog source
  const idFor = (p: any) => p.dbId || p.id;

  const save = async () => {
    setSaving(true);
    const payload = {
      id: 1,
      ...s,
      headline_es: s.headline_es || null,
      headline_en: s.headline_en || null,
      subtitle_es: s.subtitle_es || null,
      subtitle_en: s.subtitle_en || null,
      brand_name: s.brand_name || null,
      brand_tagline_es: s.brand_tagline_es || null,
      brand_tagline_en: s.brand_tagline_en || null,
      brand_cta_es: s.brand_cta_es || null,
      brand_cta_en: s.brand_cta_en || null,
      brand_logo_url: s.brand_logo_url || null,
    };
    const { error } = await supabase
      .from("hero_settings" as any)
      .upsert(payload, { onConflict: "id" });
    setSaving(false);
    if (error) {
      toast?.(lang === "es" ? "Error al guardar" : "Save failed", "error");
      console.error(error);
    } else {
      toast?.(lang === "es" ? "Hero actualizado" : "Hero updated", "success");
    }
  };

  const reset = () => setS(empty);

  if (loading) {
    return <div style={{ padding: 20, color: "var(--tx3)" }}>…</div>;
  }

  const T = {
    title: lang === "es" ? "Editor del Hero (Banner principal)" : "Hero Editor",
    hint: lang === "es"
      ? "Deja un campo vacío para usar el valor automático por defecto."
      : "Leave a field empty to fall back to the automatic default.",
    featured: lang === "es" ? "Producto destacado" : "Featured product",
    deal: lang === "es" ? "Oferta del día" : "Today's deal",
    discount: lang === "es" ? "% descuento manual (opcional)" : "Manual discount % (optional)",
    brandName: lang === "es" ? "Marca destacada" : "Featured brand",
    brandTagES: "Tagline marca (ES)",
    brandTagEN: "Tagline marca (EN)",
    brandCtaES: "Texto botón marca (ES)",
    brandCtaEN: "Texto botón marca (EN)",
    brandLogo: lang === "es" ? "Logo de la marca" : "Brand logo",
    uploadLogo: lang === "es" ? "Subir logo" : "Upload logo",
    removeLogo: lang === "es" ? "Quitar" : "Remove",
    uploading: lang === "es" ? "Subiendo…" : "Uploading…",
    headlineES: "Titular Hero (ES)",
    headlineEN: "Titular Hero (EN)",
    subES: "Subtítulo Hero (ES)",
    subEN: "Subtítulo Hero (EN)",
    auto: lang === "es" ? "Automático" : "Auto",
    save: lang === "es" ? "Guardar cambios" : "Save changes",
    reset: lang === "es" ? "Resetear a auto" : "Reset to auto",
  };

  const section: React.CSSProperties = {
    background: "var(--bg2)",
    border: "1px solid var(--bd2)",
    borderRadius: 12,
    padding: 16,
    marginBottom: 14,
  };

  return (
    <div style={{ padding: 4 }}>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: 0, color: "var(--tx)" }}>{T.title}</h3>
        <p style={{ margin: "4px 0 0", color: "var(--tx3)", fontSize: 12 }}>{T.hint}</p>
      </div>

      {/* Featured product */}
      <div style={section}>
        <label style={labelStyle}>{T.featured}</label>
        <select
          style={inputStyle}
          value={s.featured_product_id ?? ""}
          onChange={e => setS({ ...s, featured_product_id: e.target.value || null })}
        >
          <option value="">— {T.auto} —</option>
          {productOptions.map(p => (
            <option key={idFor(p)} value={idFor(p)}>{productLabel(p)}</option>
          ))}
        </select>
      </div>

      {/* Headline / Subtitle */}
      <div style={section}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={labelStyle}>{T.headlineES}</label>
            <input style={inputStyle} value={s.headline_es}
              onChange={e => setS({ ...s, headline_es: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>{T.headlineEN}</label>
            <input style={inputStyle} value={s.headline_en}
              onChange={e => setS({ ...s, headline_en: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>{T.subES}</label>
            <textarea style={{ ...inputStyle, minHeight: 60 }} value={s.subtitle_es}
              onChange={e => setS({ ...s, subtitle_es: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>{T.subEN}</label>
            <textarea style={{ ...inputStyle, minHeight: 60 }} value={s.subtitle_en}
              onChange={e => setS({ ...s, subtitle_en: e.target.value })} />
          </div>
        </div>
      </div>

      {/* Deal */}
      <div style={section}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
          <div>
            <label style={labelStyle}>{T.deal}</label>
            <select
              style={inputStyle}
              value={s.deal_product_id ?? ""}
              onChange={e => setS({ ...s, deal_product_id: e.target.value || null })}
            >
              <option value="">— {T.auto} —</option>
              {productOptions.map(p => (
                <option key={idFor(p)} value={idFor(p)}>{productLabel(p)}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>{T.discount}</label>
            <input
              type="number"
              min={0}
              max={90}
              style={inputStyle}
              value={s.deal_discount_override ?? ""}
              placeholder={T.auto}
              onChange={e => setS({
                ...s,
                deal_discount_override: e.target.value === "" ? null : Number(e.target.value),
              })}
            />
          </div>
        </div>
      </div>

      {/* Brand */}
      <div style={section}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={labelStyle}>{T.brandName}</label>
            <input
              list="hero-brand-list"
              style={inputStyle}
              value={s.brand_name}
              placeholder={T.auto}
              onChange={e => setS({ ...s, brand_name: e.target.value })}
            />
            <datalist id="hero-brand-list">
              {brands.map(b => <option key={b} value={b} />)}
            </datalist>
          </div>
          <div />
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>{T.brandLogo}</label>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              {s.brand_logo_url ? (
                <div
                  style={{
                    width: 64, height: 64, borderRadius: 12,
                    background: "#0d7a5f",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: 6, flexShrink: 0,
                  }}
                >
                  <img
                    src={s.brand_logo_url}
                    alt="brand logo"
                    style={{
                      maxWidth: "100%", maxHeight: "100%",
                      objectFit: "contain",
                      mixBlendMode: "multiply",
                    }}
                  />
                </div>
              ) : null}
              <label
                style={{
                  padding: "8px 14px", borderRadius: 8,
                  background: "var(--ac)", color: "#fff",
                  fontWeight: 700, fontSize: 12,
                  cursor: uploading ? "wait" : "pointer",
                }}
              >
                {uploading ? T.uploading : T.uploadLogo}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  disabled={uploading}
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setUploading(true);
                    try {
                      const ext = (f.name.split(".").pop() || "png").toLowerCase();
                      const path = `brand-logos/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
                      const { error: upErr } = await supabase.storage
                        .from("product-images")
                        .upload(path, f, { upsert: false, contentType: f.type });
                      if (upErr) throw upErr;
                      const { data: pub } = supabase.storage
                        .from("product-images")
                        .getPublicUrl(path);
                      setS((prev) => ({ ...prev, brand_logo_url: pub.publicUrl }));
                      toast?.(lang === "es" ? "Logo subido" : "Logo uploaded", "success");
                    } catch (err: any) {
                      console.error(err);
                      toast?.(lang === "es" ? "Error al subir" : "Upload failed", "error");
                    } finally {
                      setUploading(false);
                      e.target.value = "";
                    }
                  }}
                />
              </label>
              {s.brand_logo_url ? (
                <button
                  type="button"
                  onClick={() => setS({ ...s, brand_logo_url: "" })}
                  style={{
                    padding: "8px 14px", borderRadius: 8,
                    background: "transparent", color: "var(--tx)",
                    border: "1px solid var(--bd2)", cursor: "pointer",
                    fontSize: 12, fontWeight: 600,
                  }}
                >
                  {T.removeLogo}
                </button>
              ) : null}
              <input
                style={{ ...inputStyle, flex: 1, minWidth: 200 }}
                placeholder="https://…"
                value={s.brand_logo_url}
                onChange={(e) => setS({ ...s, brand_logo_url: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label style={labelStyle}>{T.brandTagES}</label>
            <input style={inputStyle} value={s.brand_tagline_es}
              onChange={e => setS({ ...s, brand_tagline_es: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>{T.brandTagEN}</label>
            <input style={inputStyle} value={s.brand_tagline_en}
              onChange={e => setS({ ...s, brand_tagline_en: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>{T.brandCtaES}</label>
            <input style={inputStyle} value={s.brand_cta_es}
              onChange={e => setS({ ...s, brand_cta_es: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>{T.brandCtaEN}</label>
            <input style={inputStyle} value={s.brand_cta_en}
              onChange={e => setS({ ...s, brand_cta_en: e.target.value })} />
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
        <button
          onClick={save}
          disabled={saving}
          style={{
            padding: "10px 18px", borderRadius: 10, border: "none",
            background: "var(--ac)", color: "#fff", fontWeight: 700,
            cursor: saving ? "wait" : "pointer", fontSize: 13,
          }}
        >
          {saving ? "…" : T.save}
        </button>
        <button
          onClick={reset}
          style={{
            padding: "10px 18px", borderRadius: 10,
            border: "1px solid var(--bd2)", background: "transparent",
            color: "var(--tx)", fontWeight: 600, cursor: "pointer", fontSize: 13,
          }}
        >
          {T.reset}
        </button>
      </div>
    </div>
  );
}

export default HeroEditor;